import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSimpleFeed } from '@/hooks/useSimpleFeed';
import { useSimpleFeedLike } from '@/hooks/useSimpleFeedLike';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAppSettings } from '@/hooks/useAppSettings';
import { Heart, RefreshCw } from 'lucide-react';
import FeedHeader from '@/components/feed/FeedHeader';
import { ProfileGrid } from '@/components/feed/ProfileGrid';
import { BoostStoryBar } from '@/components/feed/BoostStoryBar';
import { PreLaunchSponsoredExperience } from '@/components/feed/PreLaunchSponsoredExperience';
import LoadingState from '@/components/feed/LoadingState';
import ErrorState from '@/components/feed/ErrorState';
import EmptyProfiles from '@/components/feed/EmptyProfiles';
import InconsistentState from '@/components/feed/InconsistentState';
import { FilterFloatingButton } from '@/components/feed/FilterFloatingButton';
import HierarchicalFilterModal from '@/components/feed/HierarchicalFilterModal';
import { useProfileFilters } from '@/hooks/useProfileFilters';
import { sortProfiles, filterProfiles, applyAdvancedFilters, SortOption } from '@/utils/profileUtils';
import { debugLogger } from '@/utils/debugLogger';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { toast } from 'sonner';
import { businessRulesEngine } from '@/core/BusinessRulesEngine';

const Feed = () => {
  const navigate = useNavigate();
  const {
    user,
    profileData
  } = useAuth();
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [feedTimeout, setFeedTimeout] = useState(false);
  const {
    filters,
    isOpen,
    setIsOpen,
    updateFilter,
    clearFilters,
    getActiveFiltersCount
  } = useProfileFilters();
  const {
    profiles: rawProfiles,
    userProfile,
    loading,
    error,
    updateConnectionStatus,
    getConnectionStatus,
    getProfiles,
    loadMore,
    hasMore,
    loadedCount,
    totalCount,
    resetAndRefetch
  } = useSimpleFeed({
    onLoadComplete: onboardingStatus => {
      if (!onboardingStatus.completed) {
        navigate('/main/onboarding');
      }
    }
  });

  // Infinite scroll hook
  const {
    observerTarget
  } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    isLoading: loading,
    threshold: 500
  });
  const {
    likeProfile,
    likeLimits
  } = useSimpleFeedLike();

  // Hook para configurações globais
  const {
    isAfterLaunchDate,
    settings,
    loading: settingsLoading
  } = useAppSettings();

  // Verificar e configurar Datadog user quando usuário estiver autenticado
  useEffect(() => {
    if (user?.id && user?.email && profileData?.username) {
      // Use requestIdleCallback to avoid blocking main thread
      const configureDatadog = () => {
        try {
          // Configurar Datadog RUM
          import('@datadog/browser-rum').then(({
            datadogRum
          }) => {
            const currentUser = datadogRum.getUser();

            // Se não tem email no Datadog, setar o usuário
            if (!currentUser?.email) {
              const userData = {
                id: user.id,
                email: user.email,
                name: profileData.username
              };
              datadogRum.setUser(userData);
              debugLogger.debug('datadog', 'Datadog RUM user configured:', {
                userId: user.id,
                email: user.email,
                username: profileData.username
              });
            }
          }).catch(error => {
            debugLogger.error('Feed', 'Error importing Datadog RUM:', error);
          });

          // Configurar Datadog Logs
          import('@datadog/browser-logs').then(({
            datadogLogs
          }) => {
            datadogLogs.setUser({
              id: user.id,
              email: user.email,
              name: profileData.username
            });
            debugLogger.debug('datadog', 'Datadog Logs user configured:', {
              userId: user.id,
              email: user.email,
              username: profileData.username
            });
          }).catch(error => {
            debugLogger.error('Feed', 'Error importing Datadog Logs:', error);
          });
        } catch (error) {
          debugLogger.error('Feed', 'Error configuring Datadog user:', error);
        }
      };

      // Defer Datadog configuration to avoid blocking render
      if ('requestIdleCallback' in window) {
        requestIdleCallback(configureDatadog);
      } else {
        setTimeout(configureDatadog, 0);
      }
    }
  }, [user?.id, user?.email, profileData?.username]);

  // Get transformed profiles as ProfileData[]
  // Note: Admin filters (test profiles, roles, genders) are applied automatically 
  // in the backend (get_feed_data function) when the user is an admin. 
  // No need to apply additional filtering here.
  const transformedProfiles = getProfiles();

  // Get unique interests for filter options
  const availableInterests = useMemo(() => {
    const allInterests = transformedProfiles.flatMap(profile => profile.interests || []);
    return [...new Set(allInterests)].sort();
  }, [transformedProfiles]);

  // Apply all filters and sorting
  const processedProfiles = useMemo(() => {
    if (!transformedProfiles || transformedProfiles.length === 0) return [];
    debugLogger.debug('Feed', 'Processing profiles:', {
      total: transformedProfiles.length,
      userRole: userProfile?.role,
      userOrientation: userProfile?.orientation,
      activeFilters: getActiveFiltersCount(),
      sortBy,
      sampleProfiles: transformedProfiles.slice(0, 3).map(p => ({
        username: p.username,
        height: p.height,
        marital_status: p.marital_status
      }))
    });
    debugLogger.debug('Feed', 'Active filters:', {
      alturas: filters.alturas,
      tiposCorpo: filters.tiposCorpo,
      estadoCivil: filters.estadoCivil,
      relationshipTypes: filters.relationshipTypes
    });

    // Apply basic filters (role compatibility, etc.)
    let filtered = filterProfiles(transformedProfiles, userProfile?.role, userProfile?.orientation);
    debugLogger.debug('Feed', 'After basic filters:', {
      count: filtered.length,
      removed: transformedProfiles.length - filtered.length
    });

    // Apply advanced filters
    const beforeAdvanced = filtered.length;
    filtered = applyAdvancedFilters(filtered, filters);
    debugLogger.debug('Feed', 'After advanced filters:', {
      count: filtered.length,
      removed: beforeAdvanced - filtered.length,
      remaining: filtered.slice(0, 3).map(p => ({
        username: p.username,
        height: p.height
      }))
    });

    // Apply sorting
    const sorted = sortProfiles(filtered, sortBy, userProfile?.latitude, userProfile?.longitude);
    debugLogger.debug('Feed', 'Sorting applied:', {
      sortBy,
      beforeCount: filtered.length,
      afterCount: sorted.length,
      sampleProfile: sorted[0]?.username,
      sampleDistance: sorted[0]?.distance,
      sampleCity: sorted[0]?.city
    });
    return sorted;
  }, [transformedProfiles, userProfile, filters, sortBy, getActiveFiltersCount]);

  const handleLike = useCallback(async (profileId: string) => {
    try {
      const currentStatus = getConnectionStatus(profileId);
      if (currentStatus === 'sent' || currentStatus === 'matched') {
        debugLogger.debug('Feed', 'Already liked or matched:', profileId);
        return;
      }

      // Check like limits before attempting
      if (!likeLimits.canLike) {
        const message = likeLimits.isUnlimited ? 'Erro ao verificar limites de curtidas' : `Limite diário de curtidas atingido (${likeLimits.limit})`;
        toast.error(message);
        debugLogger.warn('Feed', 'Like blocked by daily limits:', {
          profileId,
          limits: likeLimits
        });
        return;
      }
      debugLogger.debug('Feed', 'Attempting to like profile:', profileId);

      // Usar o hook useSimpleFeedLike para enviar o like para o backend
      const success = await likeProfile(profileId);
      if (success) {
        // Apenas atualizar o estado local se a operação no backend foi bem-sucedida
        updateConnectionStatus(profileId, 'sent');
        toast.success('Like enviado!');
        debugLogger.debug('Feed', 'Like sent successfully:', profileId);
      } else {
        toast.error('Erro ao enviar like');
        debugLogger.error('Feed', 'Failed to send like:', profileId);
      }
    } catch (error) {
      debugLogger.error('Feed', 'Error liking profile:', error);
      toast.error('Erro ao enviar like');
    }
  }, [getConnectionStatus, updateConnectionStatus, likeProfile, likeLimits]);
  const handleViewProfile = useCallback((username: string) => {
    navigate(`/main/profile/${username}`);
  }, [navigate]);
  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);
  const handleReauth = useCallback(() => {
    navigate('/auth');
  }, [navigate]);

  // Show like limits info in header when not unlimited
  const getLimitsDisplay = () => {
    if (likeLimits.isUnlimited) return null;
    return `${likeLimits.remaining} curtidas restantes`;
  };

  // Timeout para loading prolongado (15s)
  useEffect(() => {
    if (loading) {
      const timeoutId = setTimeout(() => {
        setFeedTimeout(true);
        debugLogger.error('Feed', 'Loading timeout após 15s');
      }, 15000);
      return () => clearTimeout(timeoutId);
    } else {
      setFeedTimeout(false);
    }
  }, [loading]);

  // Mostrar mensagem de erro se loading demorar muito
  if (loading && feedTimeout) {
    return <div className="min-h-screen bg-gradient-to-br from-black to-gray-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 border-4 border-luxury-red border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-white text-xl font-semibold mb-2">
            Carregando...
          </h2>
          <p className="text-white/70 text-base mb-6">
            O carregamento está demorando mais que o esperado. Isso pode ser causado por uma conexão lenta.
          </p>
          <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-6 py-3 bg-luxury-red hover:bg-luxury-red/90 text-white rounded-lg font-medium transition-colors">
            <RefreshCw className="h-5 w-5" />
            Recarregar Página
          </button>
        </div>
      </div>;
  }
  if (loading || settingsLoading) {
    return <LoadingState />;
  }
  if (error) {
    return <ErrorState error={error} onRetry={handleRetry} />;
  }
  if (!userProfile?.onboarding_completed) {
    return <InconsistentState onRetry={handleRetry} onReauth={handleReauth} />;
  }

  // Verificar se é usuário sponsored e estamos antes da data de lançamento
  const shouldShowPreLaunch = userProfile?.role === 'sponsored' && !isAfterLaunchDate() && settings.launch_date;
  if (shouldShowPreLaunch) {
    return <PreLaunchSponsoredExperience launchDate={settings.launch_date} />;
  }
  const handleRefresh = async () => {
    toast.info('Atualizando feed...');
    await resetAndRefetch();
  };

  return <>
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex flex-col" style={{
      contain: 'layout'
    }}>
        <FeedHeader sortBy={sortBy} onSortChange={setSortBy}>
          <button 
            onClick={handleRefresh}
            className="p-2 text-white/60 hover:text-white transition-colors rounded-lg hover:bg-white/10"
            title="Atualizar feed"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </FeedHeader>
        
        <div className="flex-1" style={{
        contain: 'layout style'
      }}>
          <div className="w-full">
            <div style={{
            contain: 'layout'
          }} className="container max-w-7xl mx-auto pt-20 px-[16px] pb-24 md:pb-8">
              
              {/* Boost Story Bar - Instagram style */}
              <BoostStoryBar
                isProfileComplete={businessRulesEngine.validateProfileComplete(profileData || {}).allowed}
                isVerified={profileData?.verification_level === 'full'}
              />
              
              {/* Like limits indicator */}
              {!likeLimits.isUnlimited && <div className="mb-4 text-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-800/50 rounded-full text-sm text-gray-300">
                    <Heart className="h-4 w-4" />
                    {getLimitsDisplay()}
                  </div>
                </div>}
              
              {processedProfiles.length === 0 ? <EmptyProfiles hasActiveFilters={getActiveFiltersCount() > 0} onClearFilters={clearFilters} activeFilters={filters} /> : <>
                  <ProfileGrid profiles={processedProfiles} onLike={handleLike} onViewProfile={handleViewProfile} getConnectionStatus={getConnectionStatus} canLike={likeLimits.canLike} sortBy={sortBy} />
                  
                  {/* Infinite Scroll Trigger */}
                  {hasMore && <div className="flex flex-col items-center gap-2 mt-4 mb-2">
                      <p className="text-white/40 text-xs">
                        Mostrando {loadedCount} de {totalCount}
                      </p>
                      {/* Elemento invisível para trigger do IntersectionObserver */}
                      <div ref={observerTarget} className="h-20 w-full flex items-center justify-center">
                        <div className="flex items-center gap-2 text-white/40">
                          <div className="animate-spin h-5 w-5 border-2 border-pink-500 border-t-transparent rounded-full" />
                          <span className="text-sm">Carregando mais perfis...</span>
                        </div>
                      </div>
                    </div>}
                  
                </>}
            </div>
          </div>
        </div>
      </div>

      <FilterFloatingButton onClick={() => setIsOpen(true)} activeFiltersCount={getActiveFiltersCount()} />

      <HierarchicalFilterModal isOpen={isOpen} onOpenChange={setIsOpen} filters={filters} onUpdateFilter={updateFilter} onClearFilters={clearFilters} availableInterests={availableInterests} rawProfiles={transformedProfiles} userRole={userProfile?.role} userOrientation={userProfile?.orientation} />
    </>;
};
export default Feed;